import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { OrderType, SortParamsType } from 'typings';
import { usePurchaseOrderCreateItemsListQuery } from 'api';
import React, { useCallback, useState } from 'react';
import { TablePaginationConfig, TableProps } from 'components';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { IItemsTable, PurchaseOrderItemDataType } from 'pages/admin/purchaseOrders/add/itemsTable/index';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useItems = (props: IItemsTable) => {
  const { filters, onRowSelect } = props;
  const [clientDrawerState, setClientDrawerState] = useState<boolean>(false);
  const [supplierDrawerState, setSupplierDrawerState] = useState<boolean>(false);
  const [purchaseDrawerState, setPurchaseDrawerState] = useState<boolean>(false);
  const [suppliersData, setSuppliersData] = useState<Record<string, any>[] | undefined>();
  const [clientsData, setClientsData] = useState<Record<string, any>[] | undefined>();
  const [orderSku, setOrderSku] = useState<string | undefined>();
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [grouping, setGrouping] = useState<string>('NO_GROUP');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], values: Record<string, any>[]) => {
      onRowSelect(selectedKeys, values);
      setSelectedRowKeys(selectedKeys);
    },
  };
  const navigate = useNavigate();
  const { data = [], isLoading } = usePurchaseOrderCreateItemsListQuery({
    sortField: sort.field,
    sortOrder: sort.order,
    filters,
    group: grouping,
  });
  const onRedirectToBrand = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
    },
    [navigate]
  );
  const onRedirectToSupplier = useCallback(
    (id: string) => navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`),
    [navigate]
  );

  const onGroupingChange = useCallback((value: any) => {
    setGrouping(value);
  }, []);
  const onClientDrawerExpand = useCallback((clients?: Record<string, any>[]) => {
    setClientsData(clients);
    setClientDrawerState((prevState) => !prevState);
  }, []);
  const onSupplierDrawerExpand = useCallback((suppliers?: Record<string, any>[]) => {
    setSuppliersData(suppliers);
    setSupplierDrawerState((prevState) => !prevState);
  }, []);
  const onPurchasePriceDrawerExpand = useCallback((sku?: string) => {
    setOrderSku(sku);
    setPurchaseDrawerState((prevState) => !prevState);
  }, []);

  const handleTableChange: TableProps<PurchaseOrderItemDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<PurchaseOrderItemDataType> | SorterResult<PurchaseOrderItemDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  return {
    data,
    handleTableChange,
    isLoading,
    onRedirectToSupplier,
    onRedirectToBrand,
    clientDrawerState,
    supplierDrawerState,
    purchaseDrawerState,
    onClientDrawerExpand,
    onSupplierDrawerExpand,
    onPurchasePriceDrawerExpand,
    suppliersData,
    rowSelection,
    clientsData,
    orderSku,
    onGroupingChange,
  };
};
